import { safeUpdateDoc, safeDeleteDoc, authedFetch } from '../utils';
import { useState, useEffect, useRef } from 'react';
import { Play, Pause, SkipBack, SkipForward, Upload, Music, Trash2, Star, Download, BarChart2, Edit2, Save, X } from 'lucide-react';
import { collection, query, where, onSnapshot, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { appId } from '../constants';

export default function MediaPlayer({ channelId, channels, authUser, allUsers, isCompact, activeUploads, handleBackgroundUpload, isGlobal = false }) {
  const [songs, setTracks] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ bpm: '', key: '', notes: '' });
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const uploadProgress = activeUploads?.[channelId || 'global'];
  const isUploading = uploadProgress !== undefined;

  const getUploaderName = (uid) => {
    return (allUsers || []).find(u => u.id === uid)?.name || 'Unknown';
  };

  const getProjectName = (cid) => {
    if (cid === 'global') return 'Global';
    const ch = (channels || []).find(c => c.id === cid);
    return ch ? ch.name : 'Project';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString();
  };

  useEffect(() => {
    let q;
    if (isGlobal) {
      q = query(
        collection(db, 'artifacts', appId, 'public', 'data', 'media'),
        where('type', '==', 'audio')
      );
    } else if (channelId) {
      q = query(
        collection(db, 'artifacts', appId, 'public', 'data', 'media'),
        where('channelId', '==', channelId),
        where('type', '==', 'audio')
      );
    } else {
      return;
    }

    const unsub = onSnapshot(q, (snap) => {
      let docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      
      if (isGlobal) {
        // Privacy filter: Only show music uploaded by the current user, or from projects they are a member of
        docs = docs.filter(d => {
          if (d.createdBy === authUser?.uid) return true;
          if (d.channelId && d.channelId !== 'global' && channels?.some(c => c.id === d.channelId)) return true;
          return false;
        });
        docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      } else {
        docs.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      }
      setTracks(docs);
    });
    return () => unsub();
  }, [channelId, isGlobal, authUser?.uid, channels]);


  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !handleBackgroundUpload) return;
    handleBackgroundUpload(file, channelId || 'global');
  };

  const togglePlay = () => {
    if (!audioRef.current || !currentTrack) return;

    // Check if playable (lossless must be converted)
    const fileName = currentTrack.title || '';
    const ext = fileName.toLowerCase().split('.').pop();
    const isLossless = ext === 'wav' || ext === 'flac';
    if (isLossless && !currentTrack.isConverted) {
      alert('This file is still being encoded for streaming. Please wait a moment.');
      return;
    }

    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const handleToggleStar = async (track) => {
    try {
      await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'media', track.id), {
        isStarred: !track.isStarred,
        starredAt: !track.isStarred ? serverTimestamp() : null
      });
    } catch (err) {
      console.error('Toggle star failed:', err);
    }
  };

  const handleEdit = (e, track) => {
    e.stopPropagation();
    setEditingId(track.id);
    setEditForm({
      bpm: track.bpm || '',
      key: track.musicKey || '',
      notes: track.notes || ''
    });
  };

  const handleSave = async (e, trackId) => {
    e.stopPropagation();
    try {
      await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'media', trackId), {
        bpm: editForm.bpm,
        musicKey: editForm.key,
        notes: editForm.notes
      });
      setEditingId(null);
    } catch (err) {
      console.error('Error saving metadata:', err);
    }
  };

  const getAuthenticatedUrl = async (url, b2FileName) => {
    if (!url || !url.includes('backblazeb2.com')) return url;
    
    let targetFileName = b2FileName;
    if (!targetFileName) {
      const match = url.match(/\/file\/[^/]+\/(.+)$/);
      if (match && match[1]) {
        targetFileName = match[1].split('?')[0];
      }
    }

    if (!targetFileName) return url;

    try {
      const res = await authedFetch('https://us-central1-project-project-30249.cloudfunctions.net/getBackblazeDownloadToken', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileNamePrefix: targetFileName,
          uid: authUser?.uid
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.authorizationToken) {
          return url + (url.includes('?') ? '&' : '?') + `Authorization=${data.authorizationToken}`;
        }
      }
    } catch (err) {
      console.error('Failed to get download token:', err);
    }
    return url;
  };

  const handleTrackSelect = async (index) => {
    const track = songs[index];
    if (!track) return;

    // Check if playable
    const fileName = track.title || '';
    const ext = fileName.toLowerCase().split('.').pop();
    const isLossless = ext === 'wav' || ext === 'flac';
    if (isLossless && !track.isConverted) {
       return; // Don't select if still encoding
    }

    setCurrentTrackIndex(index);
    setIsPlaying(true);
    
    if (audioRef.current) {
      // Use streaming URL if available (converted opus), otherwise original
      let playUrl = track.streamingUrl || track.url;
      const b2FileName = track.streamingB2FileName || track.b2FileName;

      playUrl = await getAuthenticatedUrl(playUrl, b2FileName);

      audioRef.current.src = playUrl;
      audioRef.current.play().catch(err => {
        console.error('Playback failed:', err);
        setIsPlaying(false);
      });
    }
  };

  useEffect(() => {
    const handlePlayTrack = (e) => {
      const trackId = e.detail;
      const idx = songs.findIndex(t => t.id === trackId);
      if (idx !== -1) {
        handleTrackSelect(idx);
      }
    };
    window.addEventListener('play-track', handlePlayTrack);
    return () => window.removeEventListener('play-track', handlePlayTrack);
  }, [songs]);

  const forceDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();

      // Modern API: Force Save As Dialog
      if (window.showSaveFilePicker) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: filename || 'download',
          });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          return; // Success, skip fallback
        } catch (err) {
          // If the user simply cancelled the dialog, just return
          if (err.name === 'AbortError') return;
          console.warn('showSaveFilePicker failed, falling back to standard download:', err);
        }
      }

      // Fallback: Standard Blob Download (Safari/Firefox)
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Download failed, falling back to new tab', e);
      window.open(url, '_blank');
    }
  };

  const handleDownload = async (track) => {
    if (track.streamingUrl) {
      const choice = window.confirm("Download Original Lossless file? (Cancel for Streaming/Converted version)");
      const downloadUrl = choice ? track.url : track.streamingUrl;
      const b2FileName = choice ? track.b2FileName : track.streamingB2FileName;
      const authUrl = await getAuthenticatedUrl(downloadUrl, b2FileName);
      forceDownload(authUrl, track.title || 'download');
    } else {
      const authUrl = await getAuthenticatedUrl(track.url, track.b2FileName);
      forceDownload(authUrl, track.title || 'download');
    }
  };

  const handleDelete = async (track) => {
    if (window.confirm('Are you sure? This will permanently delete the file from the server.')) {
      try {
        // 1. Delete from Backblaze if metadata exists
        if (track.b2FileId && track.b2FileName) {
          await authedFetch('https://us-central1-project-project-30249.cloudfunctions.net/deleteBackblazeFile', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileName: track.b2FileName,
              fileId: track.b2FileId,
              uid: authUser?.uid
            })
          });
        }
        // Also delete streaming file if exists
        if (track.streamingB2FileId && track.streamingB2FileName) {
           await authedFetch('https://us-central1-project-project-30249.cloudfunctions.net/deleteBackblazeFile', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileName: track.streamingB2FileName,
              fileId: track.streamingB2FileId,
              uid: authUser?.uid
            })
          });
        }

        // 2. Delete from Firestore
        await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'media', track.id));
        
        if (currentTrackIndex !== -1 && songs[currentTrackIndex]?.id === track.id) {
          setIsPlaying(false);
          setCurrentTrackIndex(-1);
        }
      } catch (err) {
        console.error('Delete failed:', err);
        alert('Failed to delete the track. Please try again.');
      }
    }
  };

  const currentTrack = songs[currentTrackIndex];

  return (
    <div className="h-full flex flex-col bg-zinc-900 text-white border-t border-zinc-800">
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => setProgress((e.target.currentTime / e.target.duration) * 100)}
        onEnded={() => {
          if (currentTrackIndex < songs.length - 1) handleTrackSelect(currentTrackIndex + 1);
          else setIsPlaying(false);
        }}
      />
      
      {/* Player Bar */}
      <div className={`h-16 flex items-center px-4 gap-4 ${isCompact ? '' : 'border-b border-white/5'}`}>
        <div className="flex items-center gap-2">
          <button onClick={() => currentTrackIndex > 0 && handleTrackSelect(currentTrackIndex - 1)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <SkipBack size={20} fill="currentColor" />
          </button>
          {(() => {
            const fileName = currentTrack?.title || '';
            const ext = fileName.toLowerCase().split('.').pop();
            const isLossless = ext === 'wav' || ext === 'flac';
            const isEncoding = isLossless && !currentTrack?.isConverted;
            
            if (isEncoding) {
              return (
                <div className="p-3 bg-zinc-800 text-zinc-500 rounded-full cursor-not-allowed flex items-center justify-center" title="Encoding Opus...">
                  <BarChart2 size={24} className="animate-pulse" />
                </div>
              );
            }
            
            return (
              <button onClick={togglePlay} className="p-3 bg-white text-black rounded-full hover:scale-105 transition-transform">
                {isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-0.5" />}
              </button>
            );
          })()}
          <button onClick={() => currentTrackIndex < songs.length - 1 && handleTrackSelect(currentTrackIndex + 1)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <SkipForward size={20} fill="currentColor" />
          </button>
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="flex justify-between items-end mb-1">
            <div className="flex flex-col min-w-0">
               <span className="text-xs font-black uppercase tracking-widest truncate min-w-0">
                 {currentTrack ? currentTrack.title : (isGlobal ? 'Global Library' : 'No track selected')}
               </span>
               {currentTrack?.stats && (
                 <span className="text-[8px] font-bold text-zinc-500 uppercase tracking-tighter truncate">
                    {currentTrack.stats}
                 </span>
               )}
            </div>
            <span className="text-[10px] font-bold text-zinc-500 tabular-nums">
              {audioRef.current ? `${Math.floor(audioRef.current.currentTime / 60)}:${String(Math.floor(audioRef.current.currentTime % 60)).padStart(2, '0')}` : '0:00'}
            </span>
          </div>
          <div 
            className="h-1.5 bg-zinc-800 rounded-full overflow-hidden cursor-pointer relative group"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const p = (e.clientX - rect.left) / rect.width;
              if (audioRef.current) audioRef.current.currentTime = p * audioRef.current.duration;
            }}
          >
            <div className="h-full bg-white transition-all" style={{ width: `${progress}%` }} />
            <div className="absolute top-0 bottom-0 left-0 bg-white/30 group-hover:block hidden" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={onFileChange}
            accept=".mp3,.wav,.flac,audio/*"
            className="hidden"
          />
          {!isGlobal && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50 min-w-[120px] justify-center"
            >
              {isUploading ? `Uploading ${uploadProgress}%` : <><Upload size={14} /> Upload</>}
            </button>
          )}
        </div>
      </div>

      {/* Playlist */}
      {!isCompact && (
        <div className="flex-1 overflow-y-auto custom-scrollbar-hidden p-2">
          <div className="flex flex-col gap-1">
            {songs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 opacity-20">
                <Music size={32} />
                <span className="text-[10px] font-black uppercase tracking-widest mt-2">
                   {isGlobal ? 'No tracks found across projects' : 'No tracks added to this project'}
                </span>
              </div>
            ) : (
              songs.map((track, idx) => (
                <div 
                  key={track.id}
                  onClick={() => handleTrackSelect(idx)}
                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors group ${currentTrackIndex === idx ? 'bg-white/10' : 'hover:bg-white/5'}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded bg-zinc-800 flex items-center justify-center shrink-0 ${currentTrackIndex === idx ? 'text-white' : 'text-zinc-600'}`}>
                      {currentTrackIndex === idx && isPlaying ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className={`text-xs font-bold truncate ${currentTrackIndex === idx ? 'text-white' : 'text-zinc-400'}`}>
                        {track.title}
                      </span>
                      {editingId === track.id ? (
                        <div className="flex items-center gap-2 mt-1" onClick={e => e.stopPropagation()}>
                          <input type="text" placeholder="BPM" value={editForm.bpm} onChange={e => setEditForm({...editForm, bpm: e.target.value})} className="w-16 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-white focus:outline-none focus:border-zinc-500" />
                          <input type="text" placeholder="Key" value={editForm.key} onChange={e => setEditForm({...editForm, key: e.target.value})} className="w-16 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-white focus:outline-none focus:border-zinc-500" />
                          <input type="text" placeholder="Notes" value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} className="flex-1 min-w-[100px] px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-white focus:outline-none focus:border-zinc-500" />
                          <button onClick={(e) => handleSave(e, track.id)} className="p-1 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-colors"><Save size={12} /></button>
                          <button onClick={(e) => { e.stopPropagation(); setEditingId(null); }} className="p-1 bg-zinc-800 text-zinc-400 rounded hover:bg-zinc-700 transition-colors"><X size={12} /></button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-zinc-500 font-medium truncate">
                          {isGlobal ? getProjectName(track.channelId) : `By ${getUploaderName(track.createdBy)}`} • {formatDate(track.createdAt)}
                          {(track.bpm || track.musicKey || track.notes) && (
                            <>
                              {' • '}
                              {track.bpm && <span className="text-zinc-400">{track.bpm} BPM </span>}
                              {track.musicKey && <span className="text-zinc-400">{track.musicKey} </span>}
                              {track.notes && <span className="text-zinc-500 italic">({track.notes})</span>}
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button
                      onClick={(e) => handleEdit(e, track)}
                      className="p-2 text-zinc-600 hover:text-white transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={14} strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStar(track);
                      }}
                      className={`p-2 transition-colors ${track.isStarred ? 'text-yellow-500' : 'text-zinc-600 hover:text-white'}`}
                      title={track.isStarred ? "Unstar" : "Star"}
                    >
                      <Star size={14} fill={track.isStarred ? "currentColor" : "none"} strokeWidth={2.5} />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(track);
                      }}
                      className="p-2 text-zinc-600 hover:text-white transition-colors"
                      title="Download"
                    >
                      <Download size={14} strokeWidth={2.5} />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(track);
                      }}
                      className="p-2 text-zinc-600 hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
